import { CheckCircle2, Ban, AlertTriangle } from 'lucide-react';

export type EligibilityStatus = 'claimable' | 'excluded' | 'needs_review';

// Whole-percent formatting: strips a trailing ".00" so "85.00" reads as "85%" but "62.50" stays
// "62.5%". Accepts the Drizzle numeric string form as well as a number.
function formatPercent(percent: number | string): string {
  const n = typeof percent === 'string' ? parseFloat(percent) : percent;
  if (!Number.isFinite(n)) return '';
  return `${Number.isInteger(n) ? n : parseFloat(n.toFixed(2))}%`;
}

const META: Record<EligibilityStatus, { label: string; Icon: typeof CheckCircle2; cls: string }> = {
  claimable: {
    label: 'Claimable',
    Icon: CheckCircle2,
    cls: 'bg-emerald-50 text-emerald-700 border-emerald-200 dark:bg-emerald-950/40 dark:text-emerald-400 dark:border-emerald-900/50',
  },
  needs_review: {
    label: 'Needs review',
    Icon: AlertTriangle,
    cls: 'bg-amber-50 text-amber-800 border-amber-200 dark:bg-amber-950/40 dark:text-amber-300 dark:border-amber-900/50',
  },
  excluded: {
    label: 'Excluded',
    Icon: Ban,
    cls: 'bg-rose-50 text-rose-700 border-rose-200 dark:bg-rose-950/40 dark:text-rose-400 dark:border-rose-900/50',
  },
};

/**
 * Eligibility chip — icon + text, never colour-only (CLAUDE.md §6.2). The claimable variant may
 * carry the apportioned percent ("Claimable · 85%"); excluded/needs_review never show a percent
 * because those lines contribute zero to the claim regardless of their stored percent.
 */
export function EligibilityChip({
  status,
  percent,
  className,
}: {
  status: EligibilityStatus;
  percent?: number | string | null;
  className?: string;
}) {
  const meta = META[status];
  const Icon = meta.Icon;
  const showPercent = status === 'claimable' && percent != null && formatPercent(percent) !== '';

  return (
    <span
      className={`inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-xs font-medium whitespace-nowrap ${meta.cls}${className ? ` ${className}` : ''}`}
    >
      <Icon className="h-3 w-3 shrink-0" aria-hidden="true" />
      {meta.label}
      {showPercent && <span className="tabular-nums">· {formatPercent(percent!)}</span>}
    </span>
  );
}
